import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import AppConfig from '../config/AppConfig';
import JwtService from '../services/JwtService';

import { onJobCreated, onJobStateChanged, onJobStatisticsChanged } from './signalR/jobEventHandlers';
import { onRobotActiveStateChanged, onRobotCreated, onRobotDeleted, onRobotStatisticsChanged, onRobotStatusChanged } from './signalR/robotEventHandlers';
import { onUserClaimsChanged, onUserDeleted, onUserRolesChanged } from './signalR/userEventHandlers';

const logLevel = process.env?.NODE_ENV === "production" ? LogLevel.None : LogLevel.Error;
let connections: HubConnection[] = [];

const initConnection = () => {
    if (JwtService.getUser() && JwtService.getUserHubGroupKey()) {
        if (!connections || connections.length === 0) {
            initAutomationHub();
            initUserHub();
            initRobotHub();
        }
    }
}

const initAutomationHub = () => {
    if (JwtService.getUserHubGroupKey() === null)
        return;

    const connBuilder = new HubConnectionBuilder();
    const connUrl = `${AppConfig.Api.Agent.Host}/automation-hub`;

    const conn = connBuilder
        .withUrl(connUrl)
        .configureLogging(logLevel)
        .withAutomaticReconnect()
        .build();

    conn.onclose(() => {
        connections = [...connections.filter(p => p.connectionId !== conn.connectionId)];
        if (JwtService.getUserHubGroupKey()) {
            initAutomationHub();
        }
    });

    conn.start().then(() => {
        if (JwtService.getUserHubGroupKey()) {
            conn.invoke("JoinGroup", JwtService.getUserHubGroupKey())
                .then(() => {
                    connections.push(conn);
                })
                .catch(() => {
                    conn.stop().then(() => {
                        initAutomationHub();
                    });
                });
        }
        else {
            conn.stop().then(() => {
                initAutomationHub();
            });
        }
    });

    conn.on('JobCreated', function (data) {
        if (data) {
            onJobCreated(data[0]);
        }
    });

    conn.on('JobStateChanged', function (data) {
        if (data) {
            onJobStateChanged(data[0]);
        }
    });

    conn.on('JobStatisticsChanged', function (data) {
        if (data) {
            onJobStatisticsChanged(data[0]);
        }
    });
}

const initUserHub = () => {
    if (JwtService.getUserHubGroupKey() === null)
        return;

    const connBuilder = new HubConnectionBuilder();
    const connUrl = `${AppConfig.Api.Identity.Host}/user-hub`;

    const conn = connBuilder
        .withUrl(connUrl)
        .configureLogging(logLevel)
        .withAutomaticReconnect()
        .build();

    conn.onclose(() => {
        connections = [...connections.filter(p => p.connectionId !== conn.connectionId)];
        if (JwtService.getUserHubGroupKey()) {
            initUserHub();
        }
    });

    conn.start().then(() => {
        if (JwtService.getUserHubGroupKey()) {
            conn.invoke("JoinGroup", JwtService.getUserHubGroupKey())
                .then(() => {
                    connections.push(conn);
                })
                .catch(() => {
                    conn.stop().then(() => {
                        initUserHub();
                    });
                });
        }
        else {
            conn.stop().then(() => {
                initUserHub();
            });
        }
    });

    conn.on("UserDeleted", function (data) {
        if (data) {
            onUserDeleted(data[0]);
        }
    });

    conn.on("UserRolesChanged", function (data) {
        if (data) {
            onUserRolesChanged(data[0]);
        }
    });

    conn.on("UserClaimsChanged", function (data) {
        if (data) {
            onUserClaimsChanged(data[0]);
        }
    });

    connections.push(conn);
}

const initRobotHub = () => {
    if (JwtService.getUserHubGroupKey() === null)
        return;

    const connBuilder = new HubConnectionBuilder();
    const connUrl = `${AppConfig.Api.Orchestrator.Host}/robot-hub`;

    const conn = connBuilder
        .withUrl(connUrl)
        .configureLogging(logLevel)
        .withAutomaticReconnect()
        .build();

    conn.onclose(() => {
        connections = [...connections.filter(p => p.connectionId !== conn.connectionId)];
        if (JwtService.getUserHubGroupKey()) {
            initRobotHub();
        }
    });

    conn.start().then(() => {
        if (JwtService.getUserHubGroupKey()) {
            conn.invoke("JoinGroup", JwtService.getUserHubGroupKey())
                .then(() => {
                    connections.push(conn);
                })
                .catch(() => {
                    conn.stop().then(() => {
                        initUserHub();
                    })
                });
        }
        else {
            conn.stop().then(() => {
                initUserHub();
            })
        }
    });

    conn.on("RobotCreated", function (data) {
        if (data) {
            onRobotCreated(data[0]);
        }
    });

    conn.on("RobotDeleted", function (data) {
        if (data) {
            onRobotDeleted(data[0]);
        }
    });

    conn.on("RobotStatusChanged", function (data) {
        if (data) {
            onRobotStatusChanged(data[0]);
        }
    });

    conn.on("RobotActiveStateChanged", function (data) {
        if (data) {
            onRobotActiveStateChanged(data[0]);
        }
    });

    conn.on("RobotStatisticsChanged", function (data) {
        if (data) {
            onRobotStatisticsChanged(data[0]);
        }
    });
}

const initSupportHub = (supportRequestId: string, onDataReceivedCallback) => {
    if (supportRequestId) {
        const connBuilder = new HubConnectionBuilder();
        const connUrl = `${AppConfig.Api.Orchestrator.Host}/support-hub`;
        const conn = new HubConnectionBuilder()
            .withUrl(connUrl)
            .withAutomaticReconnect()
            .configureLogging(logLevel)
            .build();

        conn.start().then(() => {
            conn.invoke("JoinGroup", supportRequestId);
        });

        if (onDataReceivedCallback) {
            conn.on('RequestReplied', function (data) {
                if (data) {
                    onDataReceivedCallback('RequestReplied', data[0])
                }
            });
            conn.on('RequestStarted', function (data) {
                onDataReceivedCallback('RequestStarted')
            });
            conn.on('RequestRejected', function (data) {
                onDataReceivedCallback('RequestRejected')
            });
            conn.on('RequestCancelled', function (data) {
                onDataReceivedCallback('RequestCancelled')
            });
            conn.on('RequestResolved', function (data) {
                onDataReceivedCallback('RequestResolved')
            });
        }
    }
}

const initQueueingHub = () => {
    // const connBuilder = new HubConnectionBuilder();
    // const connUrl = `${AppConfig.Api.Agent.Host}/queueing-hub`;

    // const conn = connBuilder
    //     .withUrl(connUrl)
    //     .configureLogging(logLevel)
    //     .withAutomaticReconnect()
    //     .build();

    // connections.push(conn);
}

export { initConnection, initSupportHub, connections }